import React, { useMemo, useState } from 'react';

import { ErrorLogsPopup } from '@common/components';
import { ErrorLogs } from '@common/types';
import MultiselectFormField from '@components/form-fields/MultiselectFormField';
import UploadFileFormField from '@components/form-fields/UploadFileFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useUploadAmendmentMedicalInsuredList } from '@features/production/amendment/amendment.slice';
import { FileType } from '@models/file';
import {
  downloadFileByPath,
  downloadFileFromUrl,
  generateNewDownloadLink
} from '@utils/file-utils';

import { useFormikContext } from 'formik';

import { FormValues, FormValuesCancellation } from '../types';

type Props = {
  amendmentId: string;
  disabledForm: boolean;
  enableInsuredListUpload?: boolean;
};

const GroupMedicalDetailsWidgetAtom: React.FC<Props> = ({
  amendmentId,
  disabledForm,
  enableInsuredListUpload = false
}) => {
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [errorLogs, setErrorsLogs] = useState<ErrorLogs[]>([]);
  const { values, setFieldValue } = useFormikContext<FormValues | FormValuesCancellation>();

  const uploadMedicalInsured = useUploadAmendmentMedicalInsuredList(
    amendmentId,
    setPopUpOpen,
    setErrorsLogs
  );

  const mandatoryCovers = useMemo(
    () =>
      (values?.covers ?? [])
        .filter((c) => c.isMandatory)
        .map((c) => c.policyCover)
        .filter(Boolean),
    [values?.covers]
  );

  const allCoversOptions = useMemo(
    () =>
      (values?.covers ?? []).reduce<Record<string, string>>((acc, row) => {
        if (row.policyCover && row.policyCoverName) {
          acc[row.policyCover] = row.policyCoverName;
        }

        return acc;
      }, {}),
    [values?.covers]
  );
  const document = values?.documents?.find((d) => d.documentType === 'InsuredList');

  const today = new Date().toISOString().split('T')[0];
  const errorReportFileName = `Import List of Insureds Error Report_${today}`;

  return (
    <WidgetPaper>
      <WidgetSection title="Medical Group Details">
        <MultiselectFormField
          key="group-medical-policy-covers"
          name="groupMedicalPolicyCovers"
          title="Policy Cover(s)"
          placeholder="Policy Cover"
          values={mandatoryCovers}
          disabled
          selectOptions={allCoversOptions}
          material
        />

        <UploadFileFormField
          key="group-medical-insured-list"
          name="documents"
          title="List of Insureds"
          placeholder="Upload List of Insureds"
          value={document?.documentName}
          error=""
          onBlur={() => undefined}
          onUpload={async (file) => {
            const response = await uploadMedicalInsured(file);
            if (
              response.data.production.uploadAmendmentMedicalInsuredList.length ===
              0
            ) {
              await setFieldValue('documents', [
                {
                  documentId: document?.documentId ?? '0',
                  documentName: file.name,
                  documentPath: document?.documentPath ?? '/',
                  documentType: 'InsuredList'
                }
              ]);
            }
          }}
          downloadButton={
            document?.documentId
              ? {
                  downloadUrl: document.documentPath,
                  action: () => {
                    const url = generateNewDownloadLink(document.documentId);
                    downloadFileFromUrl(url, document.documentName);
                  }
                }
              : undefined
          }
          downloadLinks={[
            {
              displayName: 'Download Template',
              downloadUrl: '',
              action: () => {
                downloadFileByPath({
                  path: 'exceltemplates',
                  documentName: 'InsuredList.xlsx'
                });
              }
            }
          ]}
          disabled={disabledForm || !enableInsuredListUpload}
          allowedFileTypes={[
            FileType.pdf,
            FileType.docx,
            FileType.jpeg,
            FileType.png,
            FileType.xlsx
          ]}
        />
      </WidgetSection>

      <ErrorLogsPopup
        title="Import List of Insureds"
        errorReportFileName={errorReportFileName}
        errorLogs={errorLogs}
        open={popUpOpen}
        onClose={() => setPopUpOpen(false)}
      />
    </WidgetPaper>
  );
};

export { GroupMedicalDetailsWidgetAtom };

